import {
  createUuid,
  SessionBlockCreateSchema,
  SessionBlockSchema,
  SessionBlockUpdateSchema,
  SessionCreateSchema,
  SessionSchema,
  SessionUpdateSchema,
  type CreateSessionBlockInput,
  type CreateSessionInput,
  type DuaSession,
  type SessionBlock,
  type UpdateSessionBlockInput,
  type UpdateSessionInput,
} from "../../domain";
import type { DuaWorkspaceDatabase } from "../database";
import { EntityNotFoundError, parseEntityId, type RepositoryOptions } from "./repository-utils";

export interface SessionWithBlocks {
  session: DuaSession;
  blocks: SessionBlock[];
}

export class SessionRepository {
  readonly #database: DuaWorkspaceDatabase;
  readonly #now: () => string;
  readonly #createId: () => string;

  constructor(database: DuaWorkspaceDatabase, options: RepositoryOptions = {}) {
    this.#database = database;
    this.#now = options.now ?? (() => new Date().toISOString());
    this.#createId = options.createId ?? createUuid;
  }

  async list(includeArchived = false, includeDeleted = false): Promise<DuaSession[]> {
    let records = await this.#database.sessions.toArray();
    if (!includeDeleted) {
      records = records.filter((record) => record.deletedAt === undefined);
    }
    if (!includeArchived) {
      records = records.filter((record) => record.archivedAt === undefined);
    }
    return records
      .map((record) => SessionSchema.parse(record))
      .sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name));
  }

  async getById(id: string, includeDeleted = false): Promise<DuaSession | undefined> {
    const record = await this.#database.sessions.get(parseEntityId(id));
    if (record === undefined || (!includeDeleted && record.deletedAt !== undefined)) {
      return undefined;
    }
    return SessionSchema.parse(record);
  }

  async getWithBlocks(id: string): Promise<SessionWithBlocks | undefined> {
    const session = await this.getById(id);
    if (session === undefined) {
      return undefined;
    }
    return { session, blocks: await this.listBlocks(session.id) };
  }

  async listBlocks(sessionId: string, includeDeleted = false): Promise<SessionBlock[]> {
    let records = await this.#database.sessionBlocks
      .where("sessionId")
      .equals(parseEntityId(sessionId))
      .toArray();
    if (!includeDeleted) {
      records = records.filter((record) => record.deletedAt === undefined);
    }
    return records
      .map((record) => SessionBlockSchema.parse(record))
      .sort((left, right) => left.sortOrder - right.sortOrder);
  }

  async create(
    input: CreateSessionInput,
    blocks: readonly Omit<CreateSessionBlockInput, "sessionId">[] = [],
  ): Promise<SessionWithBlocks> {
    const values = SessionCreateSchema.parse(input);
    const timestamp = this.#now();
    const session = SessionSchema.parse({
      id: this.#createId(),
      ...values,
      createdAt: timestamp,
      updatedAt: timestamp,
    });
    const createdBlocks = blocks.map((block, index) =>
      SessionBlockSchema.parse({
        id: this.#createId(),
        ...SessionBlockCreateSchema.parse({ ...block, sessionId: session.id }),
        sortOrder: block.sortOrder ?? index,
        createdAt: timestamp,
        updatedAt: timestamp,
      }),
    );
    await this.#database.transaction(
      "rw",
      [
        this.#database.sessions,
        this.#database.sessionBlocks,
        this.#database.collections,
        this.#database.tags,
        this.#database.people,
        this.#database.personalDuas,
      ],
      async () => {
        await this.#assertBlockSources(createdBlocks);
        await this.#database.sessions.add(session);
        if (createdBlocks.length > 0) {
          await this.#database.sessionBlocks.bulkAdd(createdBlocks);
        }
      },
    );
    return { session, blocks: createdBlocks };
  }

  async update(id: string, input: UpdateSessionInput): Promise<DuaSession> {
    const entityId = parseEntityId(id);
    const patch = SessionUpdateSchema.parse(input);
    return this.#database.transaction("rw", this.#database.sessions, async () => {
      const current = await this.#database.sessions.get(entityId);
      if (current === undefined || current.deletedAt !== undefined) {
        throw new EntityNotFoundError("Session", entityId);
      }
      const updated = SessionSchema.parse({ ...current, ...patch, updatedAt: this.#now() });
      await this.#database.sessions.put(updated);
      return updated;
    });
  }

  async replaceBlocks(
    sessionId: string,
    blocks: readonly Omit<CreateSessionBlockInput, "sessionId">[],
  ): Promise<SessionBlock[]> {
    const entityId = parseEntityId(sessionId);
    return this.#database.transaction(
      "rw",
      [
        this.#database.sessions,
        this.#database.sessionBlocks,
        this.#database.collections,
        this.#database.tags,
        this.#database.people,
        this.#database.personalDuas,
      ],
      async () => {
        const session = await this.#database.sessions.get(entityId);
        if (session === undefined || session.deletedAt !== undefined) {
          throw new EntityNotFoundError("Session", entityId);
        }
        const timestamp = this.#now();
        const created = blocks.map((block, index) =>
          SessionBlockSchema.parse({
            id: this.#createId(),
            ...SessionBlockCreateSchema.parse({ ...block, sessionId: entityId }),
            sortOrder: block.sortOrder ?? index,
            createdAt: timestamp,
            updatedAt: timestamp,
          }),
        );
        await this.#assertBlockSources(created);
        await this.#database.sessionBlocks.where("sessionId").equals(entityId).modify((block) => {
          if (block.deletedAt === undefined) {
            block.deletedAt = timestamp;
            block.deletionReason = "superseded";
            block.updatedAt = timestamp;
          }
        });
        if (created.length > 0) {
          await this.#database.sessionBlocks.bulkAdd(created);
        }
        await this.#database.sessions.update(entityId, { updatedAt: timestamp });
        return created;
      },
    );
  }

  async updateBlock(id: string, input: UpdateSessionBlockInput): Promise<SessionBlock> {
    const blockId = parseEntityId(id);
    const patch = SessionBlockUpdateSchema.parse(input);
    return this.#database.transaction(
      "rw",
      [
        this.#database.sessionBlocks,
        this.#database.collections,
        this.#database.tags,
        this.#database.people,
        this.#database.personalDuas,
      ],
      async () => {
        const current = await this.#database.sessionBlocks.get(blockId);
        if (current === undefined || current.deletedAt !== undefined) {
          throw new EntityNotFoundError("SessionBlock", blockId);
        }
        const updated = SessionBlockSchema.parse({ ...current, ...patch, updatedAt: this.#now() });
        await this.#assertBlockSources([updated]);
        await this.#database.sessionBlocks.put(updated);
        return updated;
      },
    );
  }

  async softDelete(id: string): Promise<DuaSession> {
    const entityId = parseEntityId(id);
    return this.#database.transaction(
      "rw",
      this.#database.sessions,
      this.#database.sessionBlocks,
      async () => {
        const current = await this.#database.sessions.get(entityId);
        if (current === undefined) {
          throw new EntityNotFoundError("Session", entityId);
        }
        if (current.deletedAt !== undefined) {
          return SessionSchema.parse(current);
        }
        const timestamp = this.#now();
        const deleted = SessionSchema.parse({ ...current, deletedAt: timestamp, updatedAt: timestamp });
        await this.#database.sessions.put(deleted);
        await this.#database.sessionBlocks.where("sessionId").equals(entityId).modify((block) => {
          if (block.deletedAt === undefined) {
            block.deletedAt = timestamp;
            block.deletionReason = "session_deleted";
            block.updatedAt = timestamp;
          }
        });
        return deleted;
      },
    );
  }

  async restore(id: string): Promise<DuaSession> {
    const entityId = parseEntityId(id);
    return this.#database.transaction(
      "rw",
      this.#database.sessions,
      this.#database.sessionBlocks,
      async () => {
        const current = await this.#database.sessions.get(entityId);
        if (current === undefined) {
          throw new EntityNotFoundError("Session", entityId);
        }
        if (current.deletedAt === undefined) {
          return SessionSchema.parse(current);
        }
        const timestamp = this.#now();
        const sessionDeletedAt = current.deletedAt;
        const candidate = { ...current };
        delete candidate.deletedAt;
        const restored = SessionSchema.parse({ ...candidate, updatedAt: timestamp });
        await this.#database.sessions.put(restored);
        await this.#database.sessionBlocks.where("sessionId").equals(entityId).modify((block) => {
          if (
            block.deletionReason === "session_deleted" ||
            (block.deletionReason === undefined && block.deletedAt === sessionDeletedAt)
          ) {
            delete block.deletedAt;
            delete block.deletionReason;
            block.updatedAt = timestamp;
          }
        });
        return restored;
      },
    );
  }

  async #assertBlockSources(blocks: readonly SessionBlock[]): Promise<void> {
    const sources = {
      collection: [] as string[],
      tag: [] as string[],
      person: [] as string[],
      dua: [] as string[],
    };
    for (const block of blocks) {
      if (block.sourceId === undefined) continue;
      if (block.type === "collection") sources.collection.push(block.sourceId);
      if (block.type === "tag") sources.tag.push(block.sourceId);
      if (block.type === "person") sources.person.push(block.sourceId);
      if (block.type === "dua") sources.dua.push(block.sourceId);
    }
    const checks = [
      [this.#database.collections, sources.collection, "Collection"],
      [this.#database.tags, sources.tag, "Tag"],
      [this.#database.people, sources.person, "Person"],
      [this.#database.personalDuas, sources.dua, "PersonalDua"],
    ] as const;
    for (const [table, ids, entityName] of checks) {
      if (ids.length === 0) continue;
      const records = await table.bulkGet(ids);
      for (let index = 0; index < ids.length; index += 1) {
        const id = ids[index];
        const record = records[index];
        if (id !== undefined && (record === undefined || record.deletedAt !== undefined)) {
          throw new EntityNotFoundError(entityName, id);
        }
      }
    }
  }
}
